"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { useCart } from "../../context/CartContext"
import { toast } from "react-hot-toast"
import { selectCartItems, fetchCart, selectCartLoading } from "../../redux/slices/cartSlice"
import { createOrder, selectOrderLoading, selectOrderSuccess, resetOrderState } from "../../redux/slices/orderSlice";
import "./index.css"

import apiClient from "../../api/apiClient"
import { API_ENDPOINTS } from "../../constants/apiEndpoints"

const Checkout = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  // Get cart data from Redux
  const cartItems = useSelector(selectCartItems)
  const cartLoading = useSelector(selectCartLoading)
  const user = useSelector((state) => state.auth.user)

  // Get cart context for coupon and discount
  const {
    couponCode,
    couponApplied,
    discountPercent,
    calculateSubtotal,
    calculateDiscount,
    grandCalculateTotalPrice
  } = useCart()

  // Fetch cart data when component mounts
  useEffect(() => {
    const loadCartData = async () => {
      try {
        await dispatch(fetchCart()).unwrap()
      } catch (error) {
        console.error("Failed to fetch cart:", error)
      } finally {
        setIsInitialLoad(false)
      }
    }

    loadCartData()
  }, [dispatch])

  // Calculate totals with coupon discount
  const calculateCartTotals = () => {
    if (!cartItems || cartItems.length === 0) {
      return {
        totalPrice: 0,
        totalQuantity: 0,
        originalSubtotal: 0,
        discountAmount: 0,
        subtotal: 0
      }
    }

    const originalSubtotal = calculateSubtotal()
    const discountAmount = calculateDiscount()
    const grand = grandCalculateTotalPrice();

    // const shippingRate = 99
    const subtotal = originalSubtotal - discountAmount

    return {
      totalPrice: subtotal + grand,
      totalQuantity: cartItems.reduce((total, item) => total + item.quantity, 0),
      originalSubtotal,
      discountAmount,
      subtotal
    }
  }

  const {
    // totalPrice, 
    originalSubtotal,
    discountAmount,
    subtotal
  } = calculateCartTotals()

  // Get order state from Redux
  const orderLoading = useSelector(selectOrderLoading)
  const orderSuccess = useSelector(selectOrderSuccess)

  // Form state
  const [useSameAddress, setUseSameAddress] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState("Razorpay")
  const [errors, setErrors] = useState({})

  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    firstName: user?.fullName || "",
    Phone: user?.phone || "",
    email: user?.email || "",
    zipCode: "",
    city: "",
    state: "", // empty field to be filled by user
    address: "",
  })

  const [billingAddress, setBillingAddress] = useState({
    firstName: user?.fullName || "",
    Phone: user?.phone || "",
    email: user?.email || "",
    zipCode: "",
    city: "",
    state: "", // empty field to be filled by user
    address: "",
  })

  const userState = "";

  // Redirect to success page if order is successful
  useEffect(() => {
    if (orderSuccess) {
      // Reset order state
      dispatch(resetOrderState())
      // Redirect to success page
      navigate("/order-success")
    }
  }, [orderSuccess, dispatch, navigate])

  // Check if cart is empty and redirect to cart page
  useEffect(() => {
    // Only check if cart is empty after initial load is complete
    if (!isInitialLoad && !cartLoading && (!cartItems || cartItems.length === 0)) {
      toast.error("Your cart is empty")
      navigate("/cart")
    }
  }, [cartItems, navigate, isInitialLoad, cartLoading])

  // 🔹 Fetch full address details from pincode
  const fetchFullAddressFromPincode = async (pincode) => {
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();

      if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
        const post = data[0].PostOffice[0];
        return {
          city: post.District,
          state: post.State,
          fullAddressTail: `${post.District}, ${post.State}, ${pincode}`,
        };
      } else {
        return { city: "", state: "", fullAddressTail: "" };
      }
    } catch (error) {
      console.error("Error fetching pincode details:", error);
      return { city: "", state: "", fullAddressTail: "" };
    }
  };


  // Input validation functions
  const validateName = (value, field) => {
    if (!value.trim()) return `${field} is required`
    if (value.trim().length < 2) return `${field} must be at least 2 characters`
    return ""
  }


  const validatePhone = (value) => {
    if (!value.trim()) return "Phone number is required"
    if (!/^\d{10}$/.test(value)) return "Please enter a valid 10-digit phone number"
    return ""
  }


  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required"
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address"
    return ""
  }

  const validateZipCode = (value) => {
    if (!value.trim()) return "Zip code is required"
    if (!/^\d{6}$/.test(value)) return "Please enter a valid 6-digit zip code"
    return ""
  }

  const validateCity = (value) => {
    if (!value.trim()) return "City is required"
    if (value.trim().length < 2) return "City must be at least 2 characters"
    if (!/^[A-Za-z\s\-'.]+$/.test(value))
      return "City should contain only letters, spaces, hyphens, periods and apostrophes"
    return ""
  }

  const validateAddress = (value) => {
    if (!value.trim()) return "Address is required"
    if (value.trim().length < 5) return "Address must be at least 5 characters"
    return ""
  }

  // Validate form fields
  const validateForm = () => {
    const newErrors = {}

    // Shipping address validation
    const shippingFirstNameError = validateName(shippingAddress.firstName, "Full name")
    if (shippingFirstNameError) newErrors.shippingFirstName = shippingFirstNameError

    const shippingPhoneError = validatePhone(shippingAddress.Phone, "phone number")
    if (shippingPhoneError) newErrors.shippingPhone = shippingPhoneError

    const shippingEmailError = validateEmail(shippingAddress.email)
    if (shippingEmailError) newErrors.shippingEmail = shippingEmailError

    const shippingCityError = validateCity(shippingAddress.city)
    if (shippingCityError) newErrors.shippingCity = shippingCityError

    const shippingZipCodeError = validateZipCode(shippingAddress.zipCode)
    if (shippingZipCodeError) newErrors.shippingZipCode = shippingZipCodeError

    const shippingAddressError = validateAddress(shippingAddress.address)
    if (shippingAddressError) newErrors.shippingAddress = shippingAddressError

    // Billing address validation (only if different from shipping)
    if (!useSameAddress) {
      const billingFirstNameError = validateName(billingAddress.firstName, "Full name")
      if (billingFirstNameError) newErrors.billingFirstName = billingFirstNameError

      const billingPhoneError = validatePhone(billingAddress.Phone, "phone number")
      if (billingPhoneError) newErrors.billingPhone = billingPhoneError

      const billingEmailError = validateEmail(billingAddress.email)
      if (billingEmailError) newErrors.billingEmail = billingEmailError

      const billingCityError = validateCity(billingAddress.city)
      if (billingCityError) newErrors.billingCity = billingCityError

      const billingZipCodeError = validateZipCode(billingAddress.zipCode)
      if (billingZipCodeError) newErrors.billingZipCode = billingZipCodeError

      const billingAddressError = validateAddress(billingAddress.address)
      if (billingAddressError) newErrors.billingAddress = billingAddressError
    }

    // Payment method validation
    if (!paymentMethod) newErrors.paymentMethod = "Please select a payment method"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleShippingChange = async (e) => {
    const { name, value } = e.target;

    // Prevent manual typing in city/state fields
    if (name === "city" || name === "state") return;

    setShippingAddress({
      ...shippingAddress,
      [name]: value,
    });

    // Clear validation errors when typing
    if (errors[`shipping${name.charAt(0).toUpperCase() + name.slice(1)}`]) {
      setErrors({
        ...errors,
        [`shipping${name.charAt(0).toUpperCase() + name.slice(1)}`]: "",
      });
    }

    // ✅ Auto-fetch City & State on valid pincode
    if (name === "zipCode" && /^\d{6}$/.test(value)) {
      toast.loading("Fetching location details...");

      const { city, state, fullAddressTail } = await fetchFullAddressFromPincode(value);
      toast.dismiss();

      if (city && state) {
        setShippingAddress((prev) => ({
          ...prev,
          city,
          state,
          // address: prev.address
          //   ? `${prev.address.split(",")[0]}, ${fullAddressTail}`
          //   : fullAddressTail,
        }));

        toast.success(`Auto-filled City: ${city}, State: ${state}`);
      } else {
        toast.error("Invalid or unserviceable pincode.");
      }
    }
  };


  const handleBillingChange = async (e) => {
    const { name, value } = e.target;

    // Prevent manual typing in city/state
    if (name === "city" || name === "state") return;

    setBillingAddress({
      ...billingAddress,
      [name]: value,
    });

    // Clear errors when typing
    if (errors[`billing${name.charAt(0).toUpperCase() + name.slice(1)}`]) {
      setErrors({
        ...errors,
        [`billing${name.charAt(0).toUpperCase() + name.slice(1)}`]: "",
      });
    }

    // ✅ Auto-fetch city & state when valid 6-digit pincode entered
    if (name === "zipCode" && /^\d{6}$/.test(value)) {
      toast.loading("Fetching location details...");

      const { city, state, fullAddressTail } = await fetchFullAddressFromPincode(value);
      toast.dismiss();

      if (city && state) {
        setBillingAddress((prev) => ({
          ...prev,
          city,
          state,
          // address: prev.address
          //   ? `${prev.address.split(",")[0]}, ${fullAddressTail}`
          //   : fullAddressTail,
        }));

        toast.success(`Auto-filled City: ${city}, State: ${state}`);
      } else {
        toast.error("Invalid or unserviceable pincode.");
      }
    }
  };



  // Validate field on blur
  const validateField = (fieldType, fieldName, value) => {
    let error = ""

    switch (fieldName) {
      case "firstName":
      case "lastName":
        error = validateName(value, fieldName === "firstName" ? "First name" : "Last name")
        break
      case "email":
        error = validateEmail(value)
        break
      case "city":
        error = validateCity(value)
        break
      case "zipCode":
        error = validateZipCode(value)
        break
      case "address":
        error = validateAddress(value)
        break
      default:
        break
    }

    if (error) {
      setErrors({
        ...errors,
        [`${fieldType}${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`]: error,
      })
      return false
    }
    return true
  }

  // Handle shipping checkbox change (to use same address for billing)
  const handleUseSameAddressChange = () => {
    setUseSameAddress(!useSameAddress)
    if (!useSameAddress) {
      // If the checkbox is checked, set the billing address to the shipping address
      setBillingAddress({
        ...shippingAddress,
      })

      // Clear billing errors
      const newErrors = { ...errors }
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith("billing")) {
          delete newErrors[key]
        }
      })
      setErrors(newErrors)
    }
  }

  // verifyRazorpayPayment
  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };


  // Handle Razorpay payment
  const handleRazorpayPayment = async (orderData) => {
    try {

      const isScriptLoaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!isScriptLoaded) throw new Error("Razorpay SDK failed to load.");
      const options = {
        key: "rzp_live_ZWchv9bT9bbsKh",
        amount: grandTotal * 100,
        currency: "INR",
        name: "Derby India",
        description: "Order Payment",
        image: "https://res.cloudinary.com/dzk6ng1ba/image/upload/v1744269497/Naranja_Standy_Pouch_cpjz9i.png",
        order_id: orderData.razorpayOrderId,

        handler: async (response) => {
          try {
            const payload = {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              shippingAddress: formatAddressForAPI(shippingAddress),
              phone: shippingAddress.Phone,
              email: shippingAddress.email,
              userName: shippingAddress.firstName,
            };

            const verifyRes = await apiClient.post(API_ENDPOINTS.VERIFY_PAYMENT, payload);
            if (verifyRes.data.success) {
              toast.success("Payment verified!");

              // Facebook Pixel - Track Purchase
              if (typeof window.fbq === "function") {
                window.fbq("track", "Purchase", {
                  value: grandTotal / 100, // amount in rupees
                  currency: "INR",
                  content_type: "product",
                });
              }
              navigate("/order-success");
            } else {
              toast.error("Verification failed.");
            }
          } catch (err) {
            console.error("Error verifying Razorpay payment:", err);
            toast.error("Verification failed. Please try again.");
          }
        },
        prefill: {
          name: shippingAddress.firstName,
          email: shippingAddress.email,
          contact: shippingAddress.Phone,
        },
        notes: {
          address: formatAddressForAPI(shippingAddress),
        },
        theme: {
          color: "#41559d",
        },
      };


      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Razorpay payment error:", error);
      toast.error("Failed to initiate Razorpay payment. Please try again.")
    }
  }

  // Format address for API
  const formatAddressForAPI = (addressObj) => {
    console.log("Formatting address:", addressObj);

    return `
${addressObj.address}, 
${addressObj.city}, 
${addressObj.state} - ${addressObj.zipCode}
Name: ${addressObj.firstName}
Phone: ${addressObj.Phone}
Email: ${addressObj.email}
  `.trim();
  };


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (validateForm()) {


      // Prepare order data with only the required fields
      const orderData = {
        paymentMethod: paymentMethod,
        shippingAddress: formatAddressForAPI(shippingAddress),
        couponCode: couponApplied ? couponCode : null,
        discountAmount: couponApplied ? discountAmount : 0
      }

      // Dispatch create order action
      if (paymentMethod === "COD") {
        dispatch(createOrder(orderData))
        // Facebook Pixel - Track Purchase for COD
        if (typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: orderData.discountAmount || 0,
            currency: "INR",
            content_type: "product",
          });
        }
      } else {
        // Razorpay flow
        try {
          const razorOrderRes = await apiClient.post(API_ENDPOINTS.CREATE_RAZORPAY_ORDER, orderData);

          const razorOrderData = razorOrderRes.data;

          if (razorOrderData.razorpayOrderId && razorOrderData.amount) {
            await handleRazorpayPayment(razorOrderData);
          } else {
            toast.error("Invalid Razorpay response from server.");
          }
        } catch (error) {
          console.error("❌ Razorpay order creation failed:", error);
          toast.error("Failed to create Razorpay order.");
        }
      }

    } else {
      // Scroll to the first error
      const firstError = document.querySelector(".is-invalid")
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" })
        firstError.focus()
      }
    }
  }

  // Calculate taxes (tax-inclusive method)
const calculateTaxes = (userState) => {
  const taxRate = 0.05; // 5% GST

  // Calculate taxable amount (excluding tax)
  const taxableAmount = subtotal / (1 + taxRate);

  // Total tax included
  const taxAmount = subtotal - taxableAmount;

  if (userState === "maharashtra") {
    // Split tax into SGST + CGST (2.5% each)
    return {
      taxableAmount,
      sgst: taxAmount / 2,
      cgst: taxAmount / 2,
      igst: 0,
      total: taxAmount,
    };
  } else {
    // Full tax goes to IGST (5%)
    return {
      taxableAmount,
      sgst: 0,
      cgst: 0,
      igst: taxAmount,
      total: taxAmount,
    };
  }
};

// Usage
const taxes = calculateTaxes(userState);

// Shipping logic (FREE above ₹599)
const shipping = subtotal >= 599 ? 0 : 99;

// Grand total (tax already in subtotal)
const grandTotal = subtotal + shipping;

  // Show loading state while cart data is being fetched
  if (isInitialLoad || cartLoading) {
    return (
      <div className="loader-mask">
        <div className="loader">
          <div></div>
          <div></div>
        </div>
      </div>
    )
  }

  return (
    <div className="cart-con checkout-con position-relative">
      <div className="container">
        <div className="row">
          <div className="col-lg-8 col-12">
            <div className="product-detail-box">
              <form id="contactpage" method="post" className="position-relative" onSubmit={handleSubmit}>
                <div className="billing-section">
                  <span className="address">Shipping Address:</span>
                  <div className="billing-address-form">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label htmlFor="shipping-fname">FullName name *</label>
                          <input
                            type="text"
                            className={`form_style border ${errors.shippingFirstName ? "is-invalid" : ""}`}
                            name="firstName"
                            id="shipping-fname"
                            value={shippingAddress.firstName}
                            onChange={handleShippingChange}
                            onBlur={(e) => validateField("shipping", "firstName", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.shippingFirstName && (
                            <div className="text-danger small mt-1">{errors.shippingFirstName}</div>
                          )}
                        </div>
                      </div>
                      <div className="form-group mb-3">
                        <label htmlFor="shipping-phone">Phone number *</label>
                        <input
                          type="text"
                          className={`form_style border ${errors.shippingPhone ? "is-invalid" : ""}`}
                          name="Phone"
                          id="shipping-phone"
                          value={shippingAddress.Phone}          // Changed to lowercase
                          onChange={handleShippingChange}
                          onBlur={(e) => validateField("shipping", "phone", e.target.value)}
                          required
                          style={{ boxShadow: "none" }}
                        />
                        {errors.shippingPhone && (
                          <div className="text-danger small mt-1">{errors.shippingPhone}</div>
                        )}
                      </div>

                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label>Email *</label>
                          <input
                            type="email"
                            className={`form_style border ${errors.shippingEmail ? "is-invalid" : ""}`}
                            name="email"
                            value={shippingAddress.email}
                            onChange={handleShippingChange}
                            onBlur={(e) => validateField("shipping", "email", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.shippingEmail && <div className="text-danger small mt-1">{errors.shippingEmail}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label>State *</label>
                          <input
                            type="text"
                            className={`form_style border ${errors.shippingState ? "is-invalid" : ""}`}
                            name="state"
                            value={shippingAddress.state}
                            onChange={handleShippingChange}
                            onBlur={(e) => validateField("shipping", "state", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.shippingState && (
                            <div className="text-danger small mt-1">{errors.shippingState}</div>
                          )}
                        </div>
                      </div>

                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label>City *</label>
                          <input
                            type="text"
                            className={`form_style border ${errors.shippingCity ? "is-invalid" : ""}`}
                            name="city"
                            value={shippingAddress.city}
                            onChange={handleShippingChange}
                            onBlur={(e) => validateField("shipping", "city", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.shippingCity && <div className="text-danger small mt-1">{errors.shippingCity}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label htmlFor="shipping-code">Zip/ postal code *</label>
                          <input
                            type="text"
                            className={`form_style border ${errors.shippingZipCode ? "is-invalid" : ""}`}
                            name="zipCode"
                            id="shipping-code"
                            value={shippingAddress.zipCode}
                            onChange={handleShippingChange}
                            onBlur={(e) => validateField("shipping", "zipCode", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.shippingZipCode && (
                            <div className="text-danger small mt-1">{errors.shippingZipCode}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label style={{ fontWeight: "600" }}>
                        Full Address *
                      </label>
                      <input
                        type="text"
                        className={`form_style border ${errors.shippingAddress ? "is-invalid" : ""}`}
                        name="address"
                        value={shippingAddress.address}
                        onChange={handleShippingChange}
                        onBlur={(e) => validateField("shipping", "address", e.target.value)}
                        required
                        style={{ boxShadow: "none" }}
                        placeholder="e.g. House No. 12, Green Street, Near City Park"
                      />
                      {errors.shippingAddress && (
                        <div className="text-danger small mt-1">{errors.shippingAddress}</div>
                      )}
                      <small className="text-muted">
                        City, State & Pincode will be added automatically after entering a valid pincode.
                      </small>
                    </div>


                  </div>
                </div>

                {/* Billing Address Section */}
                <div className="billing-section">
                  <span className="address">Billing Address:</span>
                  <div className="form-group mb-3">
                    <div className="d-flex align-items-center">
                      <div className="form-group float-left checkbox" style={{ border: "none", padding: "0px" }}>
                        <input
                          type="checkbox"
                          id="sameAddress"
                          checked={useSameAddress}
                          onChange={handleUseSameAddressChange}
                          style={{ boxShadow: "none", border: "1px solid #dee2e6" }}
                        />
                        <label htmlFor="sameAddress">Same as shipping address</label>
                      </div>
                    </div>
                  </div>
                  {!useSameAddress && (
                    <div className="billing-address-form">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label htmlFor="billing-fname">Full name *</label>
                            <input
                              type="text"
                              className={`form_style border ${errors.billingFirstName ? "is-invalid" : ""}`}
                              name="firstName"
                              id="billing-fname"
                              value={billingAddress.firstName}
                              onChange={handleBillingChange}
                              onBlur={(e) => validateField("billing", "firstName", e.target.value)}
                              required
                              style={{ boxShadow: "none" }}
                            />
                            {errors.billingFirstName && (
                              <div className="text-danger small mt-1">{errors.billingFirstName}</div>
                            )}
                          </div>
                        </div>
                        <div className="form-group mb-3">
                          <label htmlFor="billing-phone">Phone number *</label>
                          <input
                            type="text"
                            className={`form_style border ${errors.billingPhone ? "is-invalid" : ""}`}
                            name="Phone"
                            id="billing-phone"
                            value={billingAddress.Phone}          // Changed to lowercase
                            onChange={handleBillingChange}
                            onBlur={(e) => validateField("billing", "phone", e.target.value)}
                            required
                            style={{ boxShadow: "none" }}
                          />
                          {errors.billingPhone && (
                            <div className="text-danger small mt-1">{errors.billingPhone}</div>
                          )}
                        </div>


                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label>Email *</label>
                            <input
                              type="email"
                              className={`form_style border ${errors.billingEmail ? "is-invalid" : ""}`}
                              name="email"
                              value={billingAddress.email}
                              onChange={handleBillingChange}
                              onBlur={(e) => validateField("billing", "email", e.target.value)}
                              required
                              style={{ boxShadow: "none" }}
                            />
                            {errors.billingEmail && <div className="text-danger small mt-1">{errors.billingEmail}</div>}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label>State *</label>
                            <input
                              type="text"
                              className={`form_style border ${errors.shippingState ? "is-invalid" : ""}`}
                              name="state"
                              value={shippingAddress.state}
                              onChange={handleShippingChange}
                              onBlur={(e) => validateField("shipping", "state", e.target.value)}
                              required
                              style={{ boxShadow: "none" }}
                            />
                            {errors.shippingState && (
                              <div className="text-danger small mt-1">{errors.shippingState}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label>City *</label>
                            <input
                              type="text"
                              className={`form_style border ${errors.billingCity ? "is-invalid" : ""}`}
                              name="city"
                              value={billingAddress.city}
                              onChange={handleBillingChange}
                              onBlur={(e) => validateField("billing", "city", e.target.value)}
                              required
                              style={{ boxShadow: "none" }}
                            />
                            {errors.billingCity && <div className="text-danger small mt-1">{errors.billingCity}</div>}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label htmlFor="billing-code">Zip/ postal code *</label>
                            <input
                              type="text"
                              className={`form_style border ${errors.billingZipCode ? "is-invalid" : ""}`}
                              name="zipCode"
                              id="billing-code"
                              value={billingAddress.zipCode}
                              onChange={handleBillingChange}
                              onBlur={(e) => validateField("billing", "zipCode", e.target.value)}
                              required
                              style={{ boxShadow: "none" }}
                            />
                            {errors.billingZipCode && (
                              <div className="text-danger small mt-1">{errors.billingZipCode}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label style={{ fontWeight: "600" }}>
                          Full Address *
                        </label>
                        <input
                          type="text"
                          className={`form_style border ${errors.billingAddress ? "is-invalid" : ""}`}
                          name="address"
                          value={billingAddress.address}
                          onChange={handleBillingChange}
                          onBlur={(e) => validateField("billing", "address", e.target.value)}
                          required
                          style={{ boxShadow: "none" }}
                          placeholder="e.g. House No. 12, Green Street, Near City Park"
                        />
                        {errors.billingAddress && (
                          <div className="text-danger small mt-1">{errors.billingAddress}</div>
                        )}
                        <small className="text-muted">
                          City, State & Pincode will be added automatically after entering a valid pincode.
                        </small>
                      </div>

                    </div>
                  )}
                </div>

                <div className="lower-form mt-4">
                  <span className="address">Payment Method:</span>

                  <div className="form-group float-left checkbox mt-2">
                    <input
                      type="checkbox"
                      id="razorpay"
                      checked={paymentMethod === "Razorpay"}
                      onChange={() => {
                        setPaymentMethod("Razorpay")
                        setErrors({ ...errors, paymentMethod: "" })
                      }}
                      style={{ boxShadow: "none", border: "1px solid #dee2e6" }}
                    />
                    <label htmlFor="razorpay">Razorpay *</label>
                    {errors.paymentMethod && <div className="text-danger small mt-1">{errors.paymentMethod}</div>}
                  </div>

                  <p className="text-center text-size-14 mt-4">
                    By clicking the button, you agree to the{" "}
                    <a href="/terms" className="text-decoration-none terms-btn">
                      Terms and Conditions
                    </a>
                  </p>
                </div>
                <button type="submit" id="submit" className="submit_now text-decoration-none" disabled={orderLoading}>
                  {orderLoading ? (
                    <>
                      Processing Order...<i className="fa-solid fa-spinner fa-spin"></i>
                    </>
                  ) : (
                    <>
                      Place Order Now<i className="fa-solid fa-arrow-right"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
          <div className="col-lg-4 col-12" style={{ position: "sticky", top: "20px", height: "fit-content" }}>
            <div className="cart-total-outer">
              <div className="top-heading">
                <span className="product-items">Items</span>
                <span className="product-prices mx-4">Price</span>
              </div>
              <div className="list-items">
                <div className="itemss">
                  {cartItems.map((item, index) => (
                    <div className="each-item" key={index}>
                      <div className="product-items">
                        <span className="heading">
                          {item.quantity} x {item.productId?.name || item.name}
                        </span>
                        {/* <p className="text-size-14 mb-0">
                          {item.productId?.description?.slice(0, 30) || item.description?.slice(0, 30)}...
                        </p> */}
                      </div>
                      <div className="product-prices">
                        <span className="dollar">₹{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Show original subtotal */}
                <div className="each-item">
                  <div className="product-items">
                    <span className="heading">Subtotal</span>
                  </div>
                  <div className="product-prices">
                    <span className="dollar">₹{originalSubtotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Show discount if applied */}
                {couponApplied && (
                  <div className="each-item">
                    <div className="product-items">
                      <span className="heading">Discount ({discountPercent}%)</span>
                    </div>
                    <div className="product-prices">
                      <span className="dollar" style={{ color: "#388e3c" }}>
                        -₹{discountAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Show subtotal after discount */}
                <div className="each-item">
                  <div className="product-items">
                    <span className="heading">Total After Discount</span>
                  </div>
                  <div className="product-prices">
                    <span className="dollar">₹{subtotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Tax breakdown */}
                <div className="each-item">
                  <div className="product-items">
                    <span className="heading">Taxable Amount (Inclusive)</span>
                  </div>
                  <div className="product-prices">
                    <span className="dollar">₹{taxes.taxableAmount.toFixed(2)}</span>
                  </div>
                </div>
                {
                  userState === "maharashtra" ? (
                    <>
                      <div className="each-item">
                        <div className="product-items">
                          <span className="heading">CGST (2.5%)</span>
                        </div>
                        <div className="product-prices">
                          <span className="dollar">₹{taxes.cgst.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="each-item">
                        <div className="product-items">
                          <span className="heading">SGST (2.5%)</span>
                        </div>
                        <div className="product-prices">
                          <span className="dollar">₹{taxes.sgst.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )
                    :
                    (
                      <>
                        <div className="each-item">
                          <div className="product-items">
                            <span className="heading">IGST (5%)</span>
                          </div>
                          <div className="product-prices">
                            <span className="dollar">₹{taxes.igst.toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )
                }
                <div className="each-item">
                  <div className="product-items">
                    <span className="heading">Shipping</span>
                  </div>
                  <div className="product-prices">
                    <span className="dollar">₹{shipping.toFixed(2)}</span>
                  </div>
                </div>
                <div className="each-item">
                  <div className="product-items">
                    <span className="heading">Grand Total</span>
                  </div>
                  <div className="product-prices">
                    <span className="dollar total-price">₹{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="note">
                <i className="fa-solid fa-shield-halved"></i>
                <span className="note-text">Safe and Secure Payments, Easy Returns. 100% Authentic Products</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout